import React from 'react';
import SortableImageCard from '../../../components/SortableImageCard';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { boardsAPI } from '../../../api';

/**
 * The image grid + the inline toolbar above it (Manage Labels · Reorder ·
 * "Show final only"). Switches between a plain grid and a dnd-kit-wrapped
 * grid based on `reorderMode`. All handlers come from props — the editor
 * owns reordering state.
 */
const BoardImageGrid = ({
  t,
  board,
  sectionLabels,
  selectedImage,
  setSelectedImage,
  setLightboxIndex,
  setShowLabelsModal,

  // Reorder
  reorderMode,
  setReorderMode,
  sensors,
  handleDragEnd,

  // Filtering
  filterFinalOnly,
  setFilterFinalOnly,

  // Per-image handlers
  handleDeleteImage,
  handleUpdateImage,
  handleCreateLabelAndAssign,
  handleToggleFinal,
  handleSaveOwnerReply,
  getReactionIcon,

  // Display flags
  featureAccess,
  reactionMode,
  brandName,
  showFilenames = false,
  fetchBoard,
  showToast,
}) => {
  const [renaming, setRenaming] = React.useState(null); // section id being renamed
  const gallerySections = Array.isArray(board.gallery_sections) ? board.gallery_sections : [];
  const orderedSections = [...gallerySections].sort((a, b) => (a.order || 0) - (b.order || 0));
  const allImages = board.images || [];
  const filteredImages = filterFinalOnly ? allImages.filter((i) => i.is_final) : allImages;

  const imagesInSection = (sectionId) => filteredImages.filter((img) => img.gallery_section_id === sectionId);
  const uncategorizedImages = filteredImages.filter((img) => !img.gallery_section_id
    || !orderedSections.some((s) => s.id === img.gallery_section_id));

  const handleAddSection = async () => {
    try {
      await boardsAPI.gallerySections.create(board.id, { title: t.gallery_new_section_default_title || 'New section', description: '' });
      fetchBoard && fetchBoard();
    } catch (e) { showToast && showToast(e.response?.data?.detail || 'Could not add section'); }
  };
  const handleRenameSection = async (sectionId, nextTitle, nextDesc) => {
    try {
      const payload = {};
      if (nextTitle !== undefined) payload.title = nextTitle;
      if (nextDesc !== undefined) payload.description = nextDesc;
      await boardsAPI.gallerySections.update(board.id, sectionId, payload);
      fetchBoard && fetchBoard();
    } catch (e) { showToast && showToast(e.response?.data?.detail || 'Could not save section'); }
  };
  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm(t.gallery_section_delete_confirm || 'Delete this section? Its images stay on the board.')) return;
    try {
      await boardsAPI.gallerySections.remove(board.id, sectionId);
      fetchBoard && fetchBoard();
    } catch (e) { showToast && showToast(e.response?.data?.detail || 'Could not delete section'); }
  };
  const handleReorderSections = async (newIds) => {
    try {
      await boardsAPI.gallerySections.reorder(board.id, newIds);
      fetchBoard && fetchBoard();
    } catch (e) { showToast && showToast(e.response?.data?.detail || 'Could not reorder'); }
  };
  const handleMoveImageToSection = async (imageId, sectionId) => {
    try {
      await boardsAPI.gallerySections.assignImage(board.id, imageId, sectionId || '');
      fetchBoard && fetchBoard();
    } catch (e) { showToast && showToast(e.response?.data?.detail || 'Could not move image'); }
  };

  // DnD sensors for section-order dragging.
  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedSections.map((s) => s.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    handleReorderSections(arrayMove(ids, oldIdx, newIdx));
  };

  const renderImageCard = (image, index) => (
    <SortableImageCard
      key={image.id}
      image={image}
      index={index}
      isSelected={selectedImage?.id === image.id}
      onDelete={handleDeleteImage}
      onImageClick={() => {
        const realIndex = allImages.findIndex((img) => img.id === image.id);
        setLightboxIndex(realIndex);
      }}
      onFooterClick={setSelectedImage}
      getReactionIcon={getReactionIcon}
      reorderEnabled={false}
      sectionLabels={sectionLabels}
      onUpdateLabel={(imageId, label) => handleUpdateImage(imageId, { section_label: label })}
      onCreateLabel={handleCreateLabelAndAssign}
      onToggleFinal={handleToggleFinal}
      onSaveOwnerReply={handleSaveOwnerReply}
      allowImageConversations={featureAccess.allow_image_conversations !== false}
      reactionMode={reactionMode}
      brandName={brandName}
      clientName={board.client_name || ''}
      isCover={board.cover_image_id === image.id}
      sharedDecisionMode={board.shared_decision_mode}
      participants={board.shared_decision_participants}
      showFilename={showFilenames}
      gallerySections={orderedSections}
      currentSectionId={image.gallery_section_id || null}
      onMoveToSection={(sectionId) => handleMoveImageToSection(image.id, sectionId)}
    />
  );
  return (
    <>
      {/* Manage Labels + Reorder Mode + Final Filter — ghost text-links */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        marginBottom: '14px', gap: '6px 22px', flexWrap: 'wrap',
      }}>
        {board.images?.some((img) => img.is_final) && (
          <button
            onClick={() => setFilterFinalOnly((prev) => !prev)}
            data-testid="filter-final-btn"
            type="button"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: '12px',
              color: filterFinalOnly ? '#C9A96E' : 'var(--kahla-text-light)',
              letterSpacing: '0.04em',
              marginRight: 'auto',
              fontFamily: 'inherit',
              transition: 'color 0.2s',
            }}
          >
            {filterFinalOnly ? '★ Final only' : '☆ Show final only'}
          </button>
        )}
        {reorderMode ? (
          <button
            onClick={() => setReorderMode(false)}
            type="button"
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: '12px', color: 'var(--kahla-charcoal)', fontWeight: 500,
              letterSpacing: '0.04em', fontFamily: 'inherit',
              textDecoration: 'underline',
              textDecorationColor: 'var(--kahla-charcoal)',
              textUnderlineOffset: '3px',
            }}
            data-testid="done-reorder-btn"
          >
            Done reordering
          </button>
        ) : (
          <button
            onClick={() => setReorderMode(true)}
            type="button"
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: '12px', color: 'var(--kahla-taupe)', letterSpacing: '0.04em',
              fontFamily: 'inherit',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(166,152,136,0.35)',
              textUnderlineOffset: '3px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
            data-testid="enable-reorder-btn"
          >
            {t.reorder_images}
          </button>
        )}
        <button
          onClick={() => setShowLabelsModal(true)}
          type="button"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: '12px', color: 'var(--kahla-taupe)', letterSpacing: '0.04em',
            fontFamily: 'inherit',
            textDecoration: 'underline',
            textDecorationColor: 'rgba(166,152,136,0.35)',
            textUnderlineOffset: '3px',
            transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
          data-testid="manage-labels-btn-main"
        >
          {t.manage_labels}
        </button>
      </div>

      {/* Reorder Mode Indicator */}
      {reorderMode && (
        <div style={{
          padding: '10px 16px', marginBottom: '16px',
          backgroundColor: 'var(--kahla-cream)', border: '1px dashed var(--kahla-charcoal)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }} data-testid="reorder-mode-banner">
          <span style={{ fontSize: '13px', color: 'var(--kahla-text)', letterSpacing: '0.02em' }}>
            Reorder mode active — drag images to rearrange
          </span>
          <button
            onClick={() => setReorderMode(false)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '12px', color: 'var(--kahla-charcoal)', fontWeight: '600',
              textDecoration: 'underline',
            }}
          >
            Done
          </button>
        </div>
      )}

      {/* Images Grid */}
      {board.images?.length === 0 ? (
        <div className="empty-state" style={{ padding: '40px' }}>
          <p style={{ color: 'var(--kahla-text-light)' }}>{t.no_images_yet}</p>
        </div>
      ) : reorderMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={board.images?.map((img) => img.id) || []}
            strategy={rectSortingStrategy}
          >
            <div className="moodboard-grid">
              {board.images?.map((image, index) => (
                <SortableImageCard
                  key={image.id}
                  image={image}
                  index={index}
                  isSelected={selectedImage?.id === image.id}
                  onDelete={handleDeleteImage}
                  onImageClick={setLightboxIndex}
                  onFooterClick={setSelectedImage}
                  getReactionIcon={getReactionIcon}
                  reorderEnabled={true}
                  sectionLabels={sectionLabels}
                  onUpdateLabel={(imageId, label) => handleUpdateImage(imageId, { section_label: label })}
                  onCreateLabel={handleCreateLabelAndAssign}
                  onToggleFinal={handleToggleFinal}
                  onSaveOwnerReply={handleSaveOwnerReply}
                  allowImageConversations={featureAccess.allow_image_conversations !== false}
                  reactionMode={reactionMode}
                  brandName={brandName}
                  clientName={board.client_name || ''}
                  sharedDecisionMode={board.shared_decision_mode}
                  participants={board.shared_decision_participants}
                  isCover={board.cover_image_id === image.id}
                  showFilename={showFilenames}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div>
          {/* Uncategorized bucket — always first, no header (legacy look). */}
          {uncategorizedImages.length > 0 && (
            <div className="moodboard-grid" data-testid="gallery-uncategorized">
              {uncategorizedImages.map((image) => renderImageCard(image, allImages.indexOf(image)))}
            </div>
          )}

          {/* Gallery Sections — DnD-sortable headers + per-section grids. */}
          {orderedSections.length > 0 && (
            <DndContext sensors={sectionSensors} collisionDetection={closestCenter} onDragEnd={onSectionDragEnd}>
              <SortableContext items={orderedSections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {orderedSections.map((section) => (
                  <GallerySectionBlock
                    key={section.id}
                    section={section}
                    images={imagesInSection(section.id)}
                    renderImageCard={renderImageCard}
                    allImages={allImages}
                    isRenaming={renaming === section.id}
                    startRenaming={() => setRenaming(section.id)}
                    stopRenaming={() => setRenaming(null)}
                    onRename={(title, desc) => handleRenameSection(section.id, title, desc)}
                    onDelete={() => handleDeleteSection(section.id)}
                    t={t}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}

          {/* + Add Section CTA */}
          <button
            type="button"
            onClick={handleAddSection}
            data-testid="add-gallery-section-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 18px', marginTop: '20px',
              background: 'transparent', border: '1px dashed var(--kahla-taupe, #a69888)',
              cursor: 'pointer', fontFamily: 'Playfair Display, serif',
              fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'var(--kahla-charcoal)', fontWeight: 500,
              transition: 'background 200ms ease, border-color 200ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--kahla-cream-light, #f7f3ea)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span aria-hidden="true" style={{ fontSize: '14px' }}>+</span>
            <span>{t.gallery_add_section || 'Add section'}</span>
          </button>
        </div>
      )}
    </>
  );
};

export default BoardImageGrid;

/**
 * A single gallery section — sortable header row (title + optional
 * description + delete button + drag handle) followed by its grid of
 * images. Title/description are edited inline via defaultValue+blur so
 * we skip local state churn while typing. Deleting a section only
 * unlinks it from images; the images drop back into the "no section"
 * bucket at the top.
 */
const GallerySectionBlock = ({
  section,
  images,
  renderImageCard,
  allImages,
  isRenaming,
  startRenaming,
  stopRenaming,
  onRename,
  onDelete,
  t,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginTop: '32px',
    padding: '20px 0 8px',
    borderTop: '1px solid var(--kahla-border)',
    background: isDragging ? 'rgba(255,255,255,0.9)' : undefined,
    boxShadow: isDragging ? '0 8px 28px rgba(42,38,32,0.14)' : undefined,
    zIndex: isDragging ? 5 : 'auto',
  };
  return (
    <section ref={setNodeRef} style={style} data-testid={`gallery-section-${section.id}`}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        marginBottom: '14px',
      }}>
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={t.gallery_section_drag || 'Reorder section'}
          title={t.gallery_section_drag || 'Reorder section'}
          data-testid={`gallery-section-drag-${section.id}`}
          style={{
            marginTop: '4px', width: '18px', height: '22px',
            background: 'transparent', border: 'none', padding: 0,
            cursor: isDragging ? 'grabbing' : 'grab',
            color: 'var(--kahla-taupe, #a69888)', flexShrink: 0,
            touchAction: 'none',
          }}
        >
          <svg width="12" height="18" viewBox="0 0 12 18" aria-hidden="true">
            <circle cx="3" cy="3"  r="1.2" fill="currentColor" />
            <circle cx="9" cy="3"  r="1.2" fill="currentColor" />
            <circle cx="3" cy="9"  r="1.2" fill="currentColor" />
            <circle cx="9" cy="9"  r="1.2" fill="currentColor" />
            <circle cx="3" cy="15" r="1.2" fill="currentColor" />
            <circle cx="9" cy="15" r="1.2" fill="currentColor" />
          </svg>
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          {isRenaming ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input
                type="text"
                defaultValue={section.title || ''}
                autoFocus
                maxLength={80}
                data-testid={`gallery-section-title-input-${section.id}`}
                onBlur={(e) => { onRename(e.target.value, undefined); stopRenaming(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') stopRenaming(); }}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: '20px',
                  fontFamily: 'Playfair Display, serif', letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: 'var(--kahla-charcoal)',
                  background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)',
                  outline: 'none',
                }}
              />
              <textarea
                defaultValue={section.description || ''}
                rows={2}
                maxLength={500}
                placeholder={t.gallery_section_desc_placeholder || 'Optional description'}
                data-testid={`gallery-section-desc-input-${section.id}`}
                onBlur={(e) => onRename(undefined, e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', fontSize: '13px', fontFamily: 'inherit',
                  fontStyle: 'italic', color: 'var(--kahla-text-light)',
                  background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)',
                  outline: 'none', resize: 'vertical', minHeight: '48px',
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={startRenaming}
              data-testid={`gallery-section-header-${section.id}`}
              style={{
                display: 'block', textAlign: 'left', width: '100%',
                background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              }}
            >
              <h3 style={{
                margin: 0, fontFamily: 'Playfair Display, serif',
                fontSize: '18px', letterSpacing: '0.24em',
                textTransform: 'uppercase', color: 'var(--kahla-charcoal)',
                fontWeight: 500,
              }}>
                {section.title || (t.gallery_new_section_default_title || 'New section')}
              </h3>
              {section.description && (
                <p style={{
                  margin: '6px 0 0', fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                  fontStyle: 'italic', fontSize: '14px', color: 'var(--kahla-text-light)',
                  lineHeight: 1.55, maxWidth: '640px',
                }}>
                  {section.description}
                </p>
              )}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onDelete}
          aria-label={t.gallery_section_delete || 'Delete section'}
          title={t.gallery_section_delete || 'Delete section'}
          data-testid={`gallery-section-delete-${section.id}`}
          style={{
            marginTop: '2px', padding: '4px 8px',
            background: 'transparent', border: '1px solid transparent',
            cursor: 'pointer', color: 'var(--kahla-text-light)',
            fontSize: '14px', fontFamily: 'inherit',
            transition: 'color 200ms ease, border-color 200ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#A04141'; e.currentTarget.style.borderColor = 'var(--kahla-border)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-text-light)'; e.currentTarget.style.borderColor = 'transparent'; }}
        >
          ×
        </button>
      </div>

      {images.length > 0 ? (
        <div className="moodboard-grid">
          {images.map((image) => renderImageCard(image, allImages.indexOf(image)))}
        </div>
      ) : (
        <div style={{
          padding: '24px', textAlign: 'center', color: 'var(--kahla-text-light)',
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic', fontSize: '14px',
          border: '1px dashed var(--kahla-border)',
        }}>
          {t.gallery_section_empty_hint || 'Use "Move to section" on any image to place it here.'}
        </div>
      )}
    </section>
  );
};
